// source: template-project@7007a338a apps/backoffice/src/components/ui/shadcn/card.tsx
import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

/**
 * Card elevation (DESIGN_SYSTEM.md §4.3): cards are flat with a hairline; a
 * shadow belongs to menus, popovers, dialogs and sheets.
 *
 * Measured 2026-10-21 before this change: the root rendered
 * `rounded-lg border bg-card text-card-foreground shadow-sm` for all ~170
 * importers — `border` is already the hairline (`--border` → `--ds-hairline`,
 * generated/shadcn-aliases.css) and `rounded-lg` → `--ds-radius-lg` (12px),
 * so the only non-v3 part of today's default is the `shadow-sm`.
 *
 * `flat` is the default (2026-10-23): the theme already painted every
 * `bg-card border` element with `box-shadow: none`, so dropping `shadow-sm`
 * from the default changes no pixels. `legacy` keeps the old classes for a
 * consumer that must opt back in while it is swept. `floating` is for the few cards that are menus in
 * disguise: it sits on `raised` (§2.1, the surface of anything that floats),
 * which also keeps it clear of the theme rule in workspace-ds-test.css that
 * flattens every `bg-card border` element (`box-shadow: none`) — on `bg-card`
 * the shadow-md would never paint (measured in the gallery, 2026-10-21).
 */
const cardVariants = cva("rounded-lg border text-card-foreground", {
  variants: {
    elevation: {
      legacy: "bg-card shadow-sm",
      flat: "border-ds-hairline bg-card",
      floating: "border-ds-hairline bg-ds-raised shadow-md",
    },
  },
  defaultVariants: { elevation: "flat" },
})

type CardProps = React.HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof cardVariants>

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, elevation, ...props }, ref) => {
    const resolved = elevation ?? "flat"
    return (
      <div
        ref={ref}
        data-slot="card"
        data-variant={resolved}
        className={cn(cardVariants({ elevation: resolved }), className)}
        {...props}
      />
    )
  }
)
Card.displayName = "Card"

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col space-y-1.5 p-6", className)}
    {...props}
  />
))
CardHeader.displayName = "CardHeader"

/**
 * `legacy` (the default) is the registry's `text-2xl font-semibold leading-none
 * tracking-tight`, kept for the current consumers. `heading` is the v3 card
 * title: `text-heading-sm` (15px / 600) alone — a type style must not be
 * stacked on the legacy weight and tracking (ds/no-type-stacking), so it is a
 * variant, not a className.
 */
const cardTitleVariants = cva("", {
  variants: {
    variant: {
      legacy: "text-2xl font-semibold leading-none tracking-tight",
      heading: "text-heading-sm text-foreground",
    },
  },
  defaultVariants: { variant: "legacy" },
})

type CardTitleProps = React.HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof cardTitleVariants>

const CardTitle = React.forwardRef<HTMLDivElement, CardTitleProps>(
  ({ className, variant, ...props }, ref) => {
    const resolved = variant ?? "legacy"
    return (
      <div
        ref={ref}
        data-slot="card-title"
        data-variant={resolved}
        className={cn(cardTitleVariants({ variant: resolved }), className)}
        {...props}
      />
    )
  }
)
CardTitle.displayName = "CardTitle"

const CardDescription = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
CardDescription.displayName = "CardDescription"

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("p-6 pt-0", className)} {...props} />
))
CardContent.displayName = "CardContent"

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex items-center p-6 pt-0", className)}
    {...props}
  />
))
CardFooter.displayName = "CardFooter"

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardDescription,
  CardContent,
  cardVariants,
  cardTitleVariants,
}
export type { CardProps, CardTitleProps }
